import api from './api';
import type { ApiResponse, PaginatedResponse, PhoneCall } from '@/types';

export interface CallFilters {
  lead_id?: string;
  user_id?: string;
  direction?: string;
  status?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  per_page?: number;
}

export interface OriginateCallData {
  lead_id?: string;
  // Omitted when the viewer lacks leads.view.phone (masked number) — the
  // backend then resolves the destination from lead_id server-side.
  to?: string;
  from_extension?: string;
  caller_id?: string;
  mode: 'desk' | 'webrtc';
  // Trusted server-side resolved outbound provider. The frontend sends ONLY
  // the provider id (never a raw prefix — toll-fraud safety); the backend
  // looks up the numeric prefix in the telephony_providers_list setting.
  provider_id?: string;
}

export const getCalls = async (filters: CallFilters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value));
    }
  });
  const response = await api.get<PaginatedResponse<PhoneCall>>(`/calls?${params.toString()}`);
  return response.data;
};

export const getLeadCalls = async (leadId: string, perPage = 50) => {
  const response = await api.get<PaginatedResponse<PhoneCall>>(`/leads/${leadId}/calls?per_page=${perPage}`);
  return response.data;
};

export const getCall = async (callId: string) => {
  const response = await api.get<ApiResponse<PhoneCall>>(`/calls/${callId}`);
  return response.data.data;
};

export const updateCall = async (callId: string, data: { disposition?: string; notes?: string }) => {
  const response = await api.patch<ApiResponse<PhoneCall>>(`/calls/${callId}`, data);
  return response.data.data;
};

export const originateCall = async (data: OriginateCallData) => {
  const response = await api.post<ApiResponse<unknown>>('/calls/originate', data);
  return response.data.data;
};

/**
 * Supervisor call-monitoring (ChanSpy) modes.
 *
 *   listen  — supervisor hears both parties, is audible to neither
 *   whisper — supervisor is audible to the agent only (coaching)
 *   barge   — supervisor joins the bridge, audible to agent AND caller
 */
export type SpyMode = 'listen' | 'whisper' | 'barge';

/**
 * Start a ChanSpy leg against an agent's extension.
 *
 * Backend: POST /calls/spy, validated by SpyRequest — `target_extension`
 * (string, max 20; server strips non-digits) + `mode` (in:listen,whisper,barge)
 * — and authorized on the `calls.monitor` permission at the FormRequest layer.
 * The resulting spy leg arrives back at the supervisor's WebRTC client as an
 * inbound INVITE whose caller-id display name is "MONITOR:<mode>:<ext>"
 * (see TelephonyProvider.onCallReceived), which auto-answers it silently.
 */
export const startCallMonitor = async (data: { target_extension: string; mode: SpyMode }) => {
  const response = await api.post<ApiResponse<unknown>>('/calls/spy', data);
  return response.data.data;
};

/** Hang up the caller's own ChanSpy leg(s) — the supervisor ext is resolved server-side. */
export const stopCallMonitor = async () => {
  const response = await api.post<ApiResponse<{ stopped?: number }>>('/calls/spy/stop');
  return response.data.data;
};

export interface SaveCallDispositionData {
  pbx_uniqueid?: string;
  extension?: string;
  direction?: string;
  from_number?: string;
  to_number?: string;
  lead_id?: string;
  disposition: string;
  notes?: string;
  follow_up_at?: string;
  follow_up_title?: string;
  // Phase 80 (CCR-04) — catalog outcome id, posted alongside the legacy
  // `disposition` string for backward-compatible reporting. Optional:
  // absent entirely on flag-off / fetch-error fallback saves.
  outcome_id?: string;
  request_id?: string;
  client_source_id?: string;
  call_id?: string;
  call_version?: number;
  call_context_token?: string;
  canonical_callback?: {
    lead_version: number;
    context_token: string;
    current_task_id: string | null;
    current_task_version: number | null;
    callback_task_id: string | null;
    callback_task_version: number | null;
    reschedule_reason?: string;
  };
}

export interface CallDispositionContext {
  call_id: string | null;
  call_version: number;
  call_context_token: string;
  callback_task: import('@/lib/tasks-api').Task | null;
  restricted_callback: boolean;
  can_manage_callback: boolean;
}

export async function getCallDispositionContext(params: { pbx_uniqueid?: string; client_source_id: string; lead_id?: string }, signal?: AbortSignal) {
  const response = await api.get<ApiResponse<CallDispositionContext>>('/calls/disposition-context', { params, signal });
  return response.data.data;
}

export const saveCallDisposition = async (data: SaveCallDispositionData) => {
  const response = await api.post<ApiResponse<PhoneCall>>('/calls/disposition', data);
  return response.data.data;
};

export const getCallRecording = async (callId: string, recordingId: string) => {
  const response = await api.get(`/calls/${callId}/recordings/${recordingId}`, {
    responseType: 'blob',
  });
  return response.data as Blob;
};

export const downloadCallRecording = async (callId: string, recordingId: string): Promise<void> => {
  const blob = await getCallRecording(callId, recordingId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `recording-${callId}.wav`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
